import { supabase } from '../lib/supabase.js';

const LOCAL_KEY = 'd24-route-reports-local-routes';
const LOCAL_SERVICE_DAYS_KEY = 'd24-route-service-days';
const LOCAL_CALENDAR_MONTHS_KEY = 'd24-route-calendar-months';
const LOCAL_CALCULATOR_SETTINGS_KEY = 'd24-line-calculator-settings';
const CALCULATION_HISTORY_TABLE = 'calculation_history';
const DIAGNOSTIC_SCREENSHOT_BUCKET = 'etoll-screenshots';
const DIAGNOSTIC_SCREENSHOT_LIMIT = 3;
const DIAGNOSTIC_SCREENSHOT_WEBP_QUALITY = 0.75;
const MONTHLY_AUTOMATION_TABLE = 'browserless_monthly_automation';


async function requireAuthenticatedSession(context = 'operacja Supabase') {
  if (!supabase) return null;

  const { data, error } = await supabase.auth.getSession();
  if (error) {
    const wrapped = new Error(`Nie udało się sprawdzić sesji Supabase: ${error.message}`);
    wrapped.code = error.code || 'AUTH_SESSION_ERROR';
    wrapped.status = error.status || 401;
    throw wrapped;
  }

  const session = data?.session || null;
  if (!session?.access_token) {
    const authError = new Error(`Brak aktywnej sesji Supabase. Zaloguj się, aby wykonać: ${context}.`);
    authError.code = 'NO_ACTIVE_AUTH_SESSION';
    authError.status = 401;
    throw authError;
  }

  return session;
}

export async function ensureOrganization() {
  if (!supabase) return 'local-demo';
  await requireAuthenticatedSession('ustalić organizację użytkownika');
  const { data, error } = await supabase.rpc('ensure_default_organization');
  if (error) throw error;
  return data;
}

export async function loadLineCalculatorSettings(orgId, routeNumber, calculatorKey) {
  const line = cleanLineNumber(routeNumber);
  const key = String(calculatorKey || '').trim();
  if (!line || !key) return null;

  if (!supabase) {
    try {
      const rows = JSON.parse(localStorage.getItem(LOCAL_CALCULATOR_SETTINGS_KEY) || '[]');
      const found = Array.isArray(rows) ? rows.find(row => (
        String(row.organization_id || '') === String(orgId || 'local-demo')
        && cleanLineNumber(row.route_number) === line
        && String(row.calculator_key || '') === key
      )) : null;
      return found?.settings && typeof found.settings === 'object' ? { ...found.settings } : null;
    } catch {
      return null;
    }
  }

  await requireAuthenticatedSession('wczytać parametry kalkulatora linii');
  const { data, error } = await supabase
    .from('line_calculator_settings')
    .select('settings')
    .eq('organization_id', orgId)
    .eq('route_number', line)
    .eq('calculator_key', key)
    .maybeSingle();
  if (error) throw error;
  return data?.settings && typeof data.settings === 'object' ? { ...data.settings } : null;
}

export async function saveLineCalculatorSettings(orgId, routeNumber, calculatorKey, settings = {}) {
  const line = cleanLineNumber(routeNumber);
  const key = String(calculatorKey || '').trim();
  if (!line) throw new Error('Brak numeru linii do zapisania parametrów kalkulatora.');
  if (!key) throw new Error('Brak klucza kalkulatora do zapisania parametrów.');

  const normalizedSettings = settings && typeof settings === 'object'
    ? JSON.parse(JSON.stringify(settings))
    : {};

  if (!supabase) {
    let rows = [];
    try {
      const parsed = JSON.parse(localStorage.getItem(LOCAL_CALCULATOR_SETTINGS_KEY) || '[]');
      rows = Array.isArray(parsed) ? parsed : [];
    } catch {}
    rows = rows.filter(row => !(
      String(row.organization_id || '') === String(orgId || 'local-demo')
      && cleanLineNumber(row.route_number) === line
      && String(row.calculator_key || '') === key
    ));
    rows.push({
      organization_id:orgId || 'local-demo',
      route_number:line,
      calculator_key:key,
      settings:normalizedSettings,
      updated_at:new Date().toISOString(),
    });
    localStorage.setItem(LOCAL_CALCULATOR_SETTINGS_KEY, JSON.stringify(rows));
    return normalizedSettings;
  }

  await requireAuthenticatedSession('zapisać parametry kalkulatora linii');
  const { error } = await supabase
    .from('line_calculator_settings')
    .upsert({
      organization_id:orgId,
      route_number:line,
      calculator_key:key,
      settings:normalizedSettings,
      updated_at:new Date().toISOString(),
    }, {
      onConflict:'organization_id,route_number,calculator_key',
    });
  if (error) throw error;
  return normalizedSettings;
}

export async function listRoutes(orgId) {
  if (!supabase) {
    return readLocalRoutes().map(record => ({
      ...record,
      carrier: record.carrier || record.snapshot?.carrier || '',
      direction_type: record.direction_type || record.snapshot?.directionType || '',
      countries: record.countries || routeCountries(record.snapshot),
      tolls_summary: record.tolls_summary || buildTollsSummary(record.snapshot),
    }));
  }

  await requireAuthenticatedSession('pobrać zapisane linie');
  const { data, error } = await supabase
    .from('routes')
    .select('id,name,route_number,direction,direction_type,carrier,countries,tolls_summary,source_filename,current_version_id,updated_at')
    .eq('organization_id', orgId)
    .order('updated_at', { ascending:false });
  if (error) throw enhanceMigrationError(error);
  return data || [];
}


function storedSectionSnapshots(version) {
  const source = version?.metadata?.section_snapshots;
  return Array.isArray(source) ? source : [];
}

function mergeStoredSectionSnapshot(row, version) {
  const order = Number(row?.section_order || 0);
  const calculatorKey = String(row?.calculator_key || '');
  const snapshot = storedSectionSnapshots(version).find(item => (
    Number(item?.order || item?.section_order || 0) === order
    && String(item?.calculatorKey || item?.calculator_key || '') === calculatorKey
  )) || storedSectionSnapshots(version).find(item => (
    Number(item?.order || item?.section_order || 0) === order
  )) || {};

  return {
    ...snapshot,
    id:row.id,
    order,
    countryCode:row.country_code,
    countryName:row.country_name,
    calculatorKey:row.calculator_key,
    status:row.status,
    points:Array.isArray(row.points) && row.points.length
      ? row.points
      : (Array.isArray(snapshot.points) ? snapshot.points : []),
  };
}

function serializableSectionSnapshots(sections = []) {
  return (Array.isArray(sections) ? sections : []).map(section => {
    try {
      return JSON.parse(JSON.stringify(section));
    } catch {
      return {
        order:section?.order,
        countryCode:section?.countryCode || '',
        countryName:section?.countryName || '',
        calculatorKey:section?.calculatorKey || '',
        displayName:section?.displayName || '',
        roadCode:section?.roadCode || '',
        roadGroup:section?.roadGroup || '',
        operatorName:section?.operatorName || '',
        tollEntryName:section?.tollEntryName || '',
        tollExitName:section?.tollExitName || '',
        isAdditionalToll:Boolean(section?.isAdditionalToll),
        points:Array.isArray(section?.points) ? section.points : [],
        distanceMeters:Number(section?.distanceMeters || 0),
        durationSeconds:Number(section?.durationSeconds || 0),
        status:section?.status || 'ready',
      };
    }
  });
}

export async function loadRouteSnapshot(routeId) {
  if (!supabase) {
    const record = readLocalRoutes().find(item => item.id === routeId);
    return record?.snapshot ? normalizeLoadedRoute(record.snapshot, record) : null;
  }

  await requireAuthenticatedSession('wczytać zapisaną linię');
  const { data: route, error: routeError } = await supabase
    .from('routes')
    .select('*')
    .eq('id', routeId)
    .single();
  if (routeError) throw enhanceMigrationError(routeError);

  const versionId = route.current_version_id;
  if (!versionId) return null;

  const [
    { data: version, error: versionError },
    { data: points, error: pointsError },
    { data: sections, error: sectionsError },
    { data: results, error: resultsError },
  ] = await Promise.all([
    supabase.from('route_versions').select('*').eq('id', versionId).single(),
    supabase.from('route_points').select('*').eq('route_version_id', versionId).order('position'),
    supabase.from('country_sections').select('*').eq('route_version_id', versionId).order('section_order'),
    supabase.from('calculation_results').select('*').eq('route_version_id', versionId).order('calculated_at', { ascending:false }),
  ]);
  if (versionError) throw versionError;
  if (pointsError) throw pointsError;
  if (sectionsError) throw sectionsError;
  if (resultsError) throw resultsError;

  const expandedPoints = (points || []).map(point => ({
    id:point.id,
    position:Number(point.position),
    name:point.name,
    address:point.address,
    lat:point.latitude,
    lon:point.longitude,
    countryCode:point.country_code,
    countryName:point.country_name,
    pointType:point.point_type,
    isBorder:point.point_type === 'border',
    sourceType:point.source_type,
    mapsUrl:point.maps_url,
    ...(point.metadata || {}),
  }));

  const routeConstraints = expandedPoints.filter(isRouteConstraint);
  const originalPoints = expandedPoints.filter(point => !point.isBorder && !isRouteConstraint(point));
  const storedRoutePlan = Array.isArray(version.metadata?.route_plan_points)
    ? version.metadata.route_plan_points
    : [];
  const routePlanPoints = storedRoutePlan.length
    ? storedRoutePlan.map(point => ({ ...point }))
    : expandedPoints.filter(point => !point.isBorder).map(point => ({ ...point }));

  const sectionModels = (sections || []).map(section => mergeStoredSectionSnapshot(section, version));
  const resultMap = await hydrateStoredScreenshots(mapResultsToSections(sectionModels, results || []));

  return {
    id:route.id,
    versionId,
    title:route.name,
    lineNumber:route.route_number || route.name || '',
    direction:route.direction,
    directionType:route.direction_type || version.metadata?.direction_type || inferDirectionType(route.direction),
    carrier:route.carrier || version.metadata?.carrier || '',
    sourceFileName:route.source_filename,
    sourceFile:null,
    originalPoints,
    routeConstraints,
    routePlanPoints,
    pointEditMode:false,
    expandedPoints,
    routeGeometry:version.geometry,
    routeLegs:[],
    sections:sectionModels,
    results:resultMap,
    countries:Array.isArray(route.countries) ? route.countries : routeCountries({ sections:sectionModels }),
    tollsSummary:Array.isArray(route.tolls_summary) ? route.tolls_summary : buildTollsSummary({ sections:sectionModels, results:resultMap }),
    mapScreenshotDataUrl:version.map_screenshot_data_url,
    distanceMeters:Number(version.metadata?.distance_meters || 0),
    durationSeconds:Number(version.metadata?.duration_seconds || 0),
    dirty:false,
  };
}

export async function saveRouteSnapshot(orgId, route) {
  const lineNumber = cleanLineNumber(route.lineNumber || deriveLineNumber(route.title));
  const countries = routeCountries(route);
  const tollsSummary = buildTollsSummary(route);
  const carrier = normalizeCarrier(route.carrier);
  const primaryDirection = normalizeDirectionType(
    route.directionType || inferDirectionType(route.direction)
  ) || 'outbound';
  const directionTypes = ['outbound', 'return'];

  if (!carrier) throw new Error('Wybierz przewoźnika INTER albo UNIBUS.');
  if (!lineNumber) throw new Error('Podaj numer linii.');

  if (!supabase) {
    let list = readLocalRoutes();
    const savedByDirection = {};

    for (const directionType of directionTypes) {
      const existing = list.find(record =>
        cleanLineNumber(record.route_number || record.snapshot?.lineNumber) === lineNumber
        && normalizeCarrier(record.carrier || record.snapshot?.carrier) === carrier
        && normalizeDirectionType(record.direction_type || record.snapshot?.directionType) === directionType
      );
      const id = directionType === primaryDirection && route.id
        ? route.id
        : existing?.id || crypto.randomUUID();
      const versionId = crypto.randomUUID();
      const snapshot = {
        ...route,
        id,
        versionId,
        lineNumber,
        carrier,
        directionType,
        countries,
        tollsSummary,
        dirty:false,
      };
      const record = {
        id,
        name:route.title,
        route_number:lineNumber,
        direction:route.direction,
        direction_type:directionType,
        carrier,
        countries,
        tolls_summary:tollsSummary,
        source_filename:route.sourceFileName,
        current_version_id:versionId,
        updated_at:new Date().toISOString(),
        snapshot,
      };
      list = [record, ...list.filter(item => item.id !== id)];
      savedByDirection[directionType] = { route_id:id, version_id:versionId };
    }

    localStorage.setItem(LOCAL_KEY, JSON.stringify(list));
    return combinedDirectionSaveResult(savedByDirection, primaryDirection);
  }

  await requireAuthenticatedSession('zapisać linię');
  if (!orgId) throw new Error('Brak organizacji użytkownika. Wyloguj się i zaloguj ponownie.');

  // Zanim save_route_snapshot przełączy current_version_id (i retencja usunie
  // starszą wersję), pobieramy najnowsze zapisane wyniki obu kierunków linii.
  // Dzięki temu zapis formularza nie może cofnąć ceny odświeżonej chwilę
  // wcześniej przez Oracle/Browserless do starszego stanu z pamięci karty.
  const preservedCalculationRows = await loadLatestLineCalculationRows(orgId, lineNumber, carrier);

  const basePayload = {
    organization_id:orgId,
    name:route.title,
    route_number:lineNumber,
    direction:route.direction,
    source_filename:route.sourceFileName,
    geometry:route.routeGeometry,
    map_screenshot_data_url:route.mapScreenshotDataUrl || '',
    points:(route.expandedPoints || []).map(point => ({
      position:point.position,
      name:point.name,
      address:point.address || '',
      latitude:point.lat,
      longitude:point.lon,
      country_code:point.countryCode || '',
      country_name:point.countryName || '',
      point_type:point.pointType || 'stop',
      source_type:point.sourceType || '',
      maps_url:point.mapsUrl || '',
      metadata:point,
    })),
    sections:(route.sections || []).map(section => ({
      section_order:section.order,
      country_code:section.countryCode,
      country_name:section.countryName,
      calculator_key:section.calculatorKey,
      status:section.status,
      points:section.points,
    })),
  };

  const savedByDirection = {};
  for (const directionType of directionTypes) {
    const payload = {
      ...basePayload,
      // Id przekazujemy wyłącznie dla aktualnie otwartego rekordu. Drugi kierunek
      // jest odnajdywany po: organizacja + linia + przewoźnik + direction_type.
      route_id:directionType === primaryDirection ? route.id : null,
      metadata:{
        distance_meters:route.distanceMeters || 0,
        duration_seconds:route.durationSeconds || 0,
        route_plan_points:Array.isArray(route.routePlanPoints) ? route.routePlanPoints : [],
        // Pełny snapshot sekcji należy do zapisanej linii. Dzięki temu po ponownym
        // otwarciu nie gubimy roadGroup, roadCode, operatora ani wjazdu/wyjazdu.
        section_snapshots:serializableSectionSnapshots(route.sections || []),
        carrier,
        direction_type:directionType,
        countries,
        shared_direction_data:true,
      },
    };

    const { data, error } = await supabase.rpc('save_route_snapshot', { p_payload:payload });
    if (error) throw enhanceMigrationError(error);
    const saved = normalizeSaveResult(data);
    if (!saved.route_id || !saved.version_id) {
      throw new Error(`Supabase nie zwrócił identyfikatora zapisanej wersji: ${directionType}.`);
    }

    const { error:updateError } = await supabase
      .from('routes')
      .update({
        route_number:lineNumber,
        carrier,
        direction_type:directionType,
        countries,
        tolls_summary:tollsSummary,
      })
      .eq('id', saved.route_id)
      .eq('organization_id', orgId);
    if (updateError) throw enhanceMigrationError(updateError);

    await replaceVersionCalculationResults(saved.version_id, route.sections || [], route.results || {}, preservedCalculationRows);

    // Automat miesięczny nie może być przywiązany na stałe do numeru sekcji
    // starej route_version. Po każdym zapisie naprawiamy jednoznaczne
    // przypisania calculator_key -> aktualny section_order. Przy wielu
    // sekcjach tego samego kalkulatora niczego nie zgadujemy.
    await rebindMonthlyAutomationSections(orgId, saved.route_id, route.sections || []);
    savedByDirection[directionType] = saved;
  }

  return combinedDirectionSaveResult(savedByDirection, primaryDirection);
}

async function rebindMonthlyAutomationSections(orgId, routeId, sections = []) {
  if (!supabase || !orgId || !routeId) return;

  let rows = [];
  try {
    const { data, error } = await supabase
      .from(MONTHLY_AUTOMATION_TABLE)
      .select('section_order,calculator_key,enabled,processing_since')
      .eq('organization_id', orgId)
      .eq('route_id', routeId);
    if (error) throw error;
    rows = data || [];
  } catch (error) {
    // Brak/awaria konfiguracji automatu nie może zablokować zapisu linii.
    console.warn('[D24 MONTHLY] Nie udało się sprawdzić przypisania automatów po zapisie linii:', error);
    return;
  }

  const currentSections = Array.isArray(sections) ? sections : [];
  for (const row of rows) {
    const calculatorKey = String(row?.calculator_key || '').trim();
    const oldOrder = Number(row?.section_order || 0);
    if (!calculatorKey || !oldOrder) continue;

    const exact = currentSections.find(section => (
      Number(section?.order || 0) === oldOrder
      && String(section?.calculatorKey || '') === calculatorKey
    ));
    if (exact) continue;

    const candidates = currentSections.filter(section => (
      String(section?.calculatorKey || '') === calculatorKey
    ));
    if (candidates.length !== 1) {
      if (row?.enabled && candidates.length > 1) {
        console.warn(`[D24 MONTHLY] Nie przenoszę automatu ${calculatorKey} dla route ${routeId}: ${candidates.length} aktualnych sekcji.`);
      }
      continue;
    }

    // Nie zmieniamy klucza rekordu, który jest właśnie wykonywany. Dispatcher
    // potrafi sam odnaleźć jego bieżącą sekcję; kolejny zapis naprawi binding.
    if (row?.processing_since) continue;

    const newOrder = Number(candidates[0]?.order || 0);
    if (!newOrder || newOrder === oldOrder) continue;

    const conflict = rows.some(other => (
      other !== row
      && Number(other?.section_order || 0) === newOrder
      && String(other?.calculator_key || '') === calculatorKey
    ));
    if (conflict) {
      console.warn(`[D24 MONTHLY] Pomijam zmianę ${calculatorKey} ${oldOrder} -> ${newOrder}: istnieje już rekord docelowy.`);
      continue;
    }

    try {
      const { error } = await supabase
        .from(MONTHLY_AUTOMATION_TABLE)
        .update({ section_order:newOrder })
        .eq('organization_id', orgId)
        .eq('route_id', routeId)
        .eq('section_order', oldOrder)
        .eq('calculator_key', calculatorKey);
      if (error) throw error;
      row.section_order = newOrder;
    } catch (error) {
      console.warn(`[D24 MONTHLY] Nie udało się przenieść automatu ${calculatorKey} ${oldOrder} -> ${newOrder}:`, error);
    }
  }
}

export async function saveCalculationResult(route, section, result) {
  if (!supabase || !route.versionId) return;
  await requireAuthenticatedSession('zapisać wynik kalkulatora');
  const calculatorKey = result.calculatorKey || section.calculatorKey || '';
  const lineNumber = cleanLineNumber(route.lineNumber || deriveLineNumber(route.title));
  const carrier = normalizeCarrier(route.carrier);

  let versionIds = [route.versionId];
  let routeIds = route.id ? [route.id] : [];

  // Jeden wynik kalkulatora zapisujemy do bieżących wersji obu kierunków.
  // RLS ogranicza zapytanie do organizacji zalogowanego użytkownika.
  if (lineNumber && carrier) {
    const { data:siblings, error:siblingError } = await supabase
      .from('routes')
      .select('id,current_version_id,direction_type')
      .eq('route_number', lineNumber)
      .eq('carrier', carrier)
      .in('direction_type', ['outbound', 'return']);
    if (siblingError) throw enhanceMigrationError(siblingError);
    versionIds = uniqueValues([
      ...versionIds,
      ...(siblings || []).map(item => item.current_version_id),
    ]);
    routeIds = uniqueValues([
      ...routeIds,
      ...(siblings || []).map(item => item.id),
    ]);
  }

  // Screenshot bieżącego wyniku trzymamy w Storage, nie jako Base64 w PostgreSQL.
  // Ten sam plik jest używany przez calculation_results i historię diagnostyczną.
  let archivedScreenshot = { bucket:'', path:'' };
  if (calculatorKey !== 'etoll-pl') {
    try {
      archivedScreenshot = await archiveDiagnosticScreenshot(route, calculatorKey, result);
      if (archivedScreenshot.path) {
        // Mutujemy ten sam obiekt wyniku, aby późniejszy zapis całej linii
        // nie skopiował ponownie dużego Base64 do nowej route_version.
        result.screenshotBucket = archivedScreenshot.bucket;
        result.screenshotPath = archivedScreenshot.path;
      }
    } catch (screenshotError) {
      // Awaria Storage nie może zablokować kalkulatora. W takim przypadku
      // calculationResultRow zachowa inline screenshot jako awaryjny fallback.
      console.warn('[D24 SCREENSHOT] Nie udało się zapisać bieżącego zrzutu w Storage:', screenshotError);
    }
  }

  for (const versionId of versionIds) {
    await upsertCalculationRowsIfNewer([
      calculationResultRow(versionId, section, result)
    ]);
  }

  if (routeIds.length) {
    const { error:updateError } = await supabase
      .from('routes')
      .update({
        countries:routeCountries(route),
        tolls_summary:buildTollsSummary(route),
      })
      .in('id', routeIds);
    if (updateError) console.warn('[D24] Nie udało się zaktualizować skrótu opłat:', updateError);
  }

  // Historia diagnostyczna jest niezależna od bieżącego calculation_results.
  // Jej awaria nie może zablokować działającego kalkulatora ani raportów.
  // e-TOLL/Oracle zapisuje historię przez trigger na calculator_jobs, dzięki
  // czemu data i cena zapiszą się również wtedy, gdy aplikacja jest zamknięta.
  // Pozostałe kraje (Browserless) archiwizujemy tutaj razem ze screenshotem.
  if (calculatorKey !== 'etoll-pl') {
    try {
      await saveCalculationHistory(route, section, {
        ...result,
        source:result.source || 'browserless',
        calculatedAt:result.calculatedAt || new Date().toISOString(),
      }, {
        archivedScreenshot,
      });
    } catch (historyError) {
      console.warn('[D24 DIAGNOSTYKA] Nie udało się zapisać historii wyniku:', historyError);
    }
  }
}

export async function saveCalculationError(route, section, details = {}) {
  if (!supabase || !route?.id || !section) return;
  const calculatorKey = details.calculatorKey || section.calculatorKey || '';
  // Błędy Oracle są rejestrowane po stronie bazy z calculator_jobs, również
  // kiedy żaden użytkownik nie ma otwartej aplikacji.
  if (calculatorKey === 'etoll-pl') return;
  try {
    await requireAuthenticatedSession('zapisać błąd kalkulatora w diagnostyce');
    await saveCalculationHistory(route, section, {
      ...details,
      calculatorKey:details.calculatorKey || section.calculatorKey || '',
      source:details.source || ((details.calculatorKey || section.calculatorKey) === 'etoll-pl' ? 'oracle' : 'browserless'),
      calculatedAt:details.calculatedAt || new Date().toISOString(),
    }, {
      status:'error',
      errorMessage:details.errorMessage || details.error || 'Nieznany błąd kalkulatora.',
      archiveScreenshot:false,
    });
  } catch (error) {
    console.warn('[D24 DIAGNOSTYKA] Nie udało się zapisać błędu:', error);
  }
}

async function saveCalculationHistory(route, section, result, options = {}) {
  if (!supabase || !route?.id || !route?.lineNumber) return null;
  const calculatorKey = result.calculatorKey || section.calculatorKey || '';
  if (!calculatorKey) return null;

  const status = options.status || 'completed';
  let archived = (
    options.archivedScreenshot
    && typeof options.archivedScreenshot === 'object'
    && options.archivedScreenshot.path
  )
    ? {
        bucket:String(options.archivedScreenshot.bucket || DIAGNOSTIC_SCREENSHOT_BUCKET),
        path:String(options.archivedScreenshot.path || ''),
      }
    : { bucket:'', path:'' };

  if (status === 'completed' && !archived.path && options.archiveScreenshot !== false) {
    try {
      archived = await archiveDiagnosticScreenshot(route, calculatorKey, result);
    } catch (error) {
      console.warn('[D24 DIAGNOSTYKA] Nie udało się zarchiwizować zrzutu:', error);
    }
  }

  const row = {
    route_id:route.id,
    route_version_id:route.versionId || null,
    route_number:cleanLineNumber(route.lineNumber || deriveLineNumber(route.title)),
    section_order:Number.isFinite(Number(section.order)) ? Number(section.order) : null,
    country_code:section.countryCode || result.countryCode || '',
    country_name:section.countryName || result.countryName || '',
    calculator_key:calculatorKey,
    cost_value:status === 'completed' ? finiteNumberOrNull(result.costValue) : null,
    cost_text:status === 'completed' ? (result.costText || '') : '',
    currency:status === 'completed' ? (result.currency || '') : '',
    distance_km:status === 'completed' ? finiteNumberOrNull(result.distanceKm) : null,
    settings_summary:result.settingsSummary || result.vehicleSettingsText || '',
    source:String(result.source || (calculatorKey === 'etoll-pl' ? 'oracle' : 'browserless')),
    status,
    error_message:options.errorMessage || '',
    job_id:String(result.serverJobId || result.jobId || ''),
    screenshot_bucket:archived.bucket || '',
    screenshot_path:archived.path || '',
    calculated_at:result.calculatedAt || new Date().toISOString(),
    details:stripScreenshots(result),
  };

  const { data, error } = await supabase
    .from(CALCULATION_HISTORY_TABLE)
    .insert(row)
    .select('id,route_number,calculator_key,screenshot_bucket,screenshot_path,created_at')
    .single();
  if (error) throw error;

  if (archived.path) {
    await rotateDiagnosticScreenshots(row.route_number, calculatorKey);
  }
  return data;
}

async function archiveDiagnosticScreenshot(route, calculatorKey, result) {
  const bucket = String(result.screenshotBucket || DIAGNOSTIC_SCREENSHOT_BUCKET).trim() || DIAGNOSTIC_SCREENSHOT_BUCKET;
  let blob = null;

  const inline = pickScreenshot(result);
  if (inline) {
    // Dla screenshotów z przeglądarki zapisujemy WebP ~75%.
    // Zachowujemy pełną rozdzielczość; zmienia się tylko format/kompresja.
    try {
      blob = await dataUrlToWebpBlob(inline, DIAGNOSTIC_SCREENSHOT_WEBP_QUALITY);
    } catch (webpError) {
      console.warn('[D24 SCREENSHOT] Konwersja WebP nie powiodła się, zapisuję format źródłowy:', webpError);
      blob = dataUrlToBlob(inline);
    }
  } else if (result?.screenshotPath) {
    const sourceBucket = String(result.screenshotBucket || DIAGNOSTIC_SCREENSHOT_BUCKET).trim() || DIAGNOSTIC_SCREENSHOT_BUCKET;
    const { data, error } = await supabase.storage.from(sourceBucket).download(String(result.screenshotPath));
    if (error) throw error;
    blob = data;
  }

  if (!blob) return { bucket:'', path:'' };

  const extension = imageExtension(blob.type);
  const line = sanitizeStoragePart(route.lineNumber || route.title || 'linia');
  const calc = sanitizeStoragePart(calculatorKey || 'kalkulator');
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  const path = `diagnostics/${line}/${calc}/${stamp}-${crypto.randomUUID()}.${extension}`;
  const { error } = await supabase.storage.from(bucket).upload(path, blob, {
    contentType:blob.type || `image/${extension}`,
    upsert:false,
    cacheControl:'31536000',
  });
  if (error) throw error;
  return { bucket, path };
}

async function rotateDiagnosticScreenshots(routeNumber, calculatorKey) {
  const { data:rows, error } = await supabase
    .from(CALCULATION_HISTORY_TABLE)
    .select('id,screenshot_bucket,screenshot_path,created_at')
    .eq('route_number', routeNumber)
    .eq('calculator_key', calculatorKey)
    .neq('screenshot_path', '')
    .order('created_at', { ascending:false })
    .limit(50);
  if (error) throw error;

  const removeRows = (rows || []).slice(DIAGNOSTIC_SCREENSHOT_LIMIT);
  if (!removeRows.length) return;

  const byBucket = new Map();
  for (const row of removeRows) {
    if (!row.screenshot_path) continue;
    const bucket = row.screenshot_bucket || DIAGNOSTIC_SCREENSHOT_BUCKET;
    if (!byBucket.has(bucket)) byBucket.set(bucket, []);
    byBucket.get(bucket).push(row.screenshot_path);
  }
  for (const [bucket, paths] of byBucket) {
    const { error:removeError } = await supabase.storage.from(bucket).remove(paths);
    if (removeError) console.warn('[D24 DIAGNOSTYKA] Nie udało się usunąć starszych zrzutów:', removeError);
  }

  const ids = removeRows.map(row => row.id).filter(Boolean);
  if (ids.length) {
    const { error:updateError } = await supabase
      .from(CALCULATION_HISTORY_TABLE)
      .update({ screenshot_bucket:'', screenshot_path:'' })
      .in('id', ids);
    if (updateError) console.warn('[D24 DIAGNOSTYKA] Nie udało się wyczyścić ścieżek starych zrzutów:', updateError);
  }
}

function dataUrlToBlob(dataUrl) {
  const match = String(dataUrl || '').match(/^data:([^;,]+)?(;base64)?,(.*)$/s);
  if (!match) throw new Error('Nieprawidłowy format zrzutu obrazu.');
  const mime = match[1] || 'image/png';
  const encoded = match[3] || '';
  if (match[2]) {
    const binary = atob(encoded);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return new Blob([bytes], { type:mime });
  }
  return new Blob([decodeURIComponent(encoded)], { type:mime });
}


async function dataUrlToWebpBlob(dataUrl, quality = DIAGNOSTIC_SCREENSHOT_WEBP_QUALITY) {
  const sourceBlob = dataUrlToBlob(dataUrl);
  if (!String(sourceBlob.type || '').startsWith('image/')) return sourceBlob;
  if (String(sourceBlob.type || '').toLowerCase() === 'image/webp') return sourceBlob;
  if (typeof createImageBitmap !== 'function') {
    throw new Error('Przeglądarka nie obsługuje createImageBitmap.');
  }

  const bitmap = await createImageBitmap(sourceBlob);
  try {
    const width = Number(bitmap.width || 0);
    const height = Number(bitmap.height || 0);
    if (!(width > 0 && height > 0)) {
      throw new Error('Screenshot nie ma prawidłowych wymiarów.');
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Brak kontekstu Canvas 2D.');

    context.drawImage(bitmap, 0, 0, width, height);

    const webp = await new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => blob ? resolve(blob) : reject(new Error('Canvas nie utworzył pliku WebP.')),
        'image/webp',
        Math.max(0.4, Math.min(0.95, Number(quality) || 0.75))
      );
    });

    if (!(webp instanceof Blob) || !webp.size) {
      throw new Error('Powstał pusty plik WebP.');
    }
    return webp;
  } finally {
    bitmap.close?.();
  }
}

function imageExtension(mime) {
  const value = String(mime || '').toLowerCase();
  if (value.includes('jpeg') || value.includes('jpg')) return 'jpg';
  if (value.includes('webp')) return 'webp';
  if (value.includes('gif')) return 'gif';
  return 'png';
}

function sanitizeStoragePart(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 90) || 'item';
}

export async function listRouteServiceDays(orgId, month) {
  const { start, end } = monthBounds(month);
  if (!supabase) {
    return readLocalServiceDays().filter(item =>
      item.organization_id === orgId
      && item.service_date >= start
      && item.service_date < end
    );
  }

  await requireAuthenticatedSession('pobrać kalendarz linii');

  // PostgREST/Supabase zwraca domyślnie maksymalnie 1000 rekordów.
  // Przy kilkudziesięciu liniach pełny miesiąc przekracza ten limit
  // i bez stronicowania kalendarz wizualnie urywa się około 18. dnia.
  const pageSize = 1000;
  const rows = [];
  const params = {
    p_organization_id:orgId,
    p_month:`${String(month).slice(0, 7)}-01`,
  };

  for (let from = 0; ; from += pageSize) {
    const { data, error } = await supabase
      .rpc('list_route_service_days', params)
      .range(from, from + pageSize - 1);

    if (error) throw enhanceMigrationError(error);

    const page = Array.isArray(data) ? data : [];
    rows.push(...page);

    if (page.length < pageSize) break;
    if (rows.length >= 50000) {
      throw new Error('Kalendarz przekroczył bezpieczny limit 50 000 wpisów dla jednego miesiąca.');
    }
  }

  return rows;
}

export async function listRouteCalendarMonths(orgId, month) {
  const monthStart = `${String(month).slice(0, 7)}-01`;
  if (!supabase) {
    return readLocalCalendarMonths().filter(item =>
      item.organization_id === orgId
      && item.month_start === monthStart
    );
  }

  await requireAuthenticatedSession('pobrać stan zapisu kalendarzy');
  const { data, error } = await supabase
    .from('route_calendar_months')
    .select('route_id,month_start,initialized_at,updated_at')
    .eq('organization_id', orgId)
    .eq('month_start', monthStart);
  if (error) throw enhanceMigrationError(error);
  return data || [];
}

export async function replaceRouteServiceDays(orgId, lineNumber, carrier, month, entries) {
  const normalizedCarrier = normalizeCarrier(carrier);
  const normalizedLine = cleanLineNumber(lineNumber);
  const normalizedEntries = (entries || [])
    .map(entry => ({
      service_date:String(entry.service_date || entry.serviceDate || ''),
      direction_type:normalizeDirectionType(entry.direction_type || entry.directionType),
    }))
    .filter(entry => /^\d{4}-\d{2}-\d{2}$/.test(entry.service_date) && entry.direction_type);

  if (!supabase) {
    const { start, end } = monthBounds(month);
    const retained = readLocalServiceDays().filter(item => !(
      item.organization_id === orgId
      && item.route_number === normalizedLine
      && item.carrier === normalizedCarrier
      && item.service_date >= start
      && item.service_date < end
    ));
    const now = new Date().toISOString();
    const inserted = normalizedEntries.map(entry => ({
      id:crypto.randomUUID(),
      organization_id:orgId,
      route_number:normalizedLine,
      carrier:normalizedCarrier,
      service_date:entry.service_date,
      direction_type:entry.direction_type,
      created_at:now,
      updated_at:now,
    }));
    localStorage.setItem(LOCAL_SERVICE_DAYS_KEY, JSON.stringify([...retained, ...inserted]));

    const routeIds = readLocalRoutes()
      .filter(record =>
        cleanLineNumber(record.route_number || record.snapshot?.lineNumber) === normalizedLine
        && normalizeCarrier(record.carrier || record.snapshot?.carrier) === normalizedCarrier
      )
      .map(record => record.id)
      .filter(Boolean);
    const monthStart = `${String(month).slice(0, 7)}-01`;
    const existingMarkers = readLocalCalendarMonths().filter(marker => !(
      marker.organization_id === orgId
      && marker.month_start === monthStart
      && routeIds.includes(marker.route_id)
    ));
    const initializedAt = new Date(Date.now() - 1).toISOString();
    const savedMarkers = routeIds.map(routeId => ({
      organization_id:orgId,
      route_id:routeId,
      month_start:monthStart,
      initialized_at:initializedAt,
      updated_at:now,
    }));
    localStorage.setItem(LOCAL_CALENDAR_MONTHS_KEY, JSON.stringify([...existingMarkers, ...savedMarkers]));
    return inserted.length;
  }

  await requireAuthenticatedSession('zapisać kalendarz linii');
  const { data, error } = await supabase.rpc('replace_route_service_days', {
    p_organization_id:orgId,
    p_route_number:normalizedLine,
    p_carrier:normalizedCarrier,
    p_month:`${String(month).slice(0, 7)}-01`,
    p_entries:normalizedEntries,
  });
  if (error) throw enhanceMigrationError(error);
  return Number(data || 0);
}

export async function deleteLine(orgId, lineNumber, carrier) {
  const normalizedCarrier = normalizeCarrier(carrier);
  const normalizedLine = cleanLineNumber(lineNumber);
  if (!normalizedCarrier) throw new Error('Nieprawidłowy przewoźnik linii.');
  if (!normalizedLine) throw new Error('Brak numeru linii do usunięcia.');

  if (!supabase) {
    const routes = readLocalRoutes();
    const removed = routes.filter(record =>
      cleanLineNumber(record.route_number || record.snapshot?.lineNumber) === normalizedLine
      && normalizeCarrier(record.carrier || record.snapshot?.carrier) === normalizedCarrier
    );
    const removedIds = removed.map(record => record.id).filter(Boolean);
    localStorage.setItem(LOCAL_KEY, JSON.stringify(routes.filter(record => !removedIds.includes(record.id))));
    localStorage.setItem(
      LOCAL_SERVICE_DAYS_KEY,
      JSON.stringify(readLocalServiceDays().filter(day => !(
        day.organization_id === orgId
        && cleanLineNumber(day.route_number) === normalizedLine
        && normalizeCarrier(day.carrier) === normalizedCarrier
      )))
    );
    localStorage.setItem(
      LOCAL_CALENDAR_MONTHS_KEY,
      JSON.stringify(readLocalCalendarMonths().filter(marker => !removedIds.includes(marker.route_id)))
    );
    if (!removedIds.length) throw new Error(`Nie znaleziono linii ${normalizedLine} (${normalizedCarrier}).`);
    return removedIds;
  }

  await requireAuthenticatedSession('usunąć linię');
  if (!orgId) throw new Error('Brak organizacji użytkownika. Wyloguj się i zaloguj ponownie.');

  const { data, error } = await supabase
    .from('routes')
    .delete()
    .eq('organization_id', orgId)
    .eq('route_number', normalizedLine)
    .eq('carrier', normalizedCarrier)
    .select('id');
  if (error) throw enhanceMigrationError(error);

  const deletedIds = (data || []).map(row => row.id).filter(Boolean);
  if (!deletedIds.length) {
    throw new Error(`Nie znaleziono linii ${normalizedLine} (${normalizedCarrier}) albo nie masz prawa jej usunąć.`);
  }
  return deletedIds;
}

export async function loadMonthlyReportData(orgId, month, carrier = '') {
  const normalizedCarrier = normalizeCarrier(carrier);
  if (!supabase) {
    const records = readLocalRoutes()
      .filter(record => !normalizedCarrier || normalizeCarrier(record.carrier || record.snapshot?.carrier) === normalizedCarrier)
      .map(record => normalizeLoadedRoute(record.snapshot, record));
    const serviceDays = (await listRouteServiceDays(orgId, month))
      .filter(day => !normalizedCarrier || day.carrier === normalizedCarrier);
    return { routes:records, serviceDays };
  }

  await requireAuthenticatedSession('utworzyć raport miesięczny');
  let routesQuery = supabase
    .from('routes')
    .select('id,name,route_number,direction,direction_type,carrier,countries,tolls_summary,source_filename,current_version_id,updated_at')
    .eq('organization_id', orgId)
    .not('current_version_id', 'is', null);
  if (normalizedCarrier) routesQuery = routesQuery.eq('carrier', normalizedCarrier);
  const { data:routeRows, error:routeError } = await routesQuery.order('updated_at', { ascending:false });
  if (routeError) throw enhanceMigrationError(routeError);

  const versionIds = (routeRows || []).map(row => row.current_version_id).filter(Boolean);
  const serviceDays = (await listRouteServiceDays(orgId, month))
    .filter(day => !normalizedCarrier || day.carrier === normalizedCarrier);
  if (!versionIds.length) return { routes:[], serviceDays };

  const [
    { data:versions, error:versionsError },
    { data:points, error:pointsError },
    { data:sections, error:sectionsError },
    { data:results, error:resultsError },
  ] = await Promise.all([
    supabase.from('route_versions').select('*').in('id', versionIds),
    supabase.from('route_points').select('*').in('route_version_id', versionIds).order('position'),
    supabase.from('country_sections').select('*').in('route_version_id', versionIds).order('section_order'),
    supabase.from('calculation_results').select('*').in('route_version_id', versionIds).order('calculated_at', { ascending:false }),
  ]);
  if (versionsError) throw versionsError;
  if (pointsError) throw pointsError;
  if (sectionsError) throw sectionsError;
  if (resultsError) throw resultsError;

  const versionById = new Map((versions || []).map(version => [version.id, version]));
  const pointsByVersion = groupBy(points || [], row => row.route_version_id);
  const sectionsByVersion = groupBy(sections || [], row => row.route_version_id);
  const resultsByVersion = groupBy(results || [], row => row.route_version_id);

  const routes = await Promise.all((routeRows || []).map(async route => {
    const version = versionById.get(route.current_version_id);
    const sectionModels = (sectionsByVersion.get(route.current_version_id) || [])
      .map(section => mergeStoredSectionSnapshot(section, version));
    const resultMap = await hydrateStoredScreenshots(mapResultsToSections(sectionModels, resultsByVersion.get(route.current_version_id) || []));
    const expandedPoints = (pointsByVersion.get(route.current_version_id) || []).map(point => ({
      id:point.id,
      position:Number(point.position),
      name:point.name,
      address:point.address,
      lat:point.latitude,
      lon:point.longitude,
      countryCode:point.country_code,
      countryName:point.country_name,
      pointType:point.point_type,
      isBorder:point.point_type === 'border',
      sourceType:point.source_type,
      mapsUrl:point.maps_url,
      ...(point.metadata || {}),
    }));
    const originalPoints = expandedPoints.filter(point => !point.isBorder && !isRouteConstraint(point));
    return {
      id:route.id,
      versionId:route.current_version_id,
      title:route.name,
      lineNumber:route.route_number || route.name || '',
      direction:route.direction || '',
      directionType:normalizeDirectionType(route.direction_type || version?.metadata?.direction_type || inferDirectionType(route.direction)),
      carrier:normalizeCarrier(route.carrier || version?.metadata?.carrier),
      sourceFileName:route.source_filename || '',
      sourceFile:null,
      originalPoints,
      expandedPoints,
      routeGeometry:version?.geometry || null,
      routeLegs:[],
      mapScreenshotDataUrl:version?.map_screenshot_data_url || '',
      countries:Array.isArray(route.countries) && route.countries.length
        ? route.countries
        : routeCountries({ sections:sectionModels }),
      tollsSummary:Array.isArray(route.tolls_summary) && route.tolls_summary.length
        ? route.tolls_summary
        : buildTollsSummary({ sections:sectionModels, results:resultMap }),
      sections:sectionModels,
      results:resultMap,
      distanceMeters:Number(version?.metadata?.distance_meters || 0),
      durationSeconds:Number(version?.metadata?.duration_seconds || 0),
      updatedAt:route.updated_at,
      dirty:false,
    };
  }));

  return { routes, serviceDays };
}

async function loadLatestLineCalculationRows(orgId, lineNumber, carrier) {
  if (!supabase || !orgId || !lineNumber || !carrier) return [];

  const { data:routeRows, error:routeError } = await supabase
    .from('routes')
    .select('current_version_id')
    .eq('organization_id', orgId)
    .eq('route_number', lineNumber)
    .eq('carrier', carrier)
    .in('direction_type', ['outbound', 'return']);
  if (routeError) throw enhanceMigrationError(routeError);

  const versionIds = uniqueValues((routeRows || []).map(row => row.current_version_id));
  if (!versionIds.length) return [];

  const { data:rows, error } = await supabase
    .from('calculation_results')
    .select('route_version_id,country_section_order,country_code,job_id,calculator_key,cost_value,cost_text,currency,distance_km,duration_text,raw_result,screenshot_data_url,status,calculated_at')
    .in('route_version_id', versionIds)
    .eq('status', 'completed')
    .order('calculated_at', { ascending:false });
  if (error) throw enhanceMigrationError(error);

  // Dwa kierunki linii mogą chwilowo mieć różne wartości. Zachowujemy tylko
  // najnowszy wynik dla section_order + calculator_key.
  const latest = new Map();
  for (const row of rows || []) {
    const key = calculationRowKey(row.country_section_order, row.calculator_key);
    if (!latest.has(key)) latest.set(key, row);
  }
  return [...latest.values()];
}

async function replaceVersionCalculationResults(versionId, sections, results, preservedRows = []) {
  if (!supabase) return;

  const validKeys = new Set((sections || []).map(section => (
    calculationRowKey(section.order, section.calculatorKey)
  )));
  const merged = new Map();

  // Najpierw wynik, który faktycznie był zapisany w bazie przed utworzeniem
  // nowej route_version.
  for (const row of preservedRows || []) {
    const key = calculationRowKey(row.country_section_order, row.calculator_key);
    if (!validKeys.has(key) || merged.has(key)) continue;
    merged.set(key, cloneStoredCalculationRow(versionId, row));
  }

  // Wynik z pamięci przeglądarki może być nowszy (np. kalkulator uruchomiony
  // ręcznie tuż przed zapisem) albo starszy od automatu. Wygrywa calculated_at.
  for (const section of sections || []) {
    const result = results?.[section.id];
    if (!result) continue;
    const candidate = calculationResultRow(versionId, section, result);
    const key = calculationRowKey(candidate.country_section_order, candidate.calculator_key);
    const current = merged.get(key);
    if (!current || calculationRowTime(candidate) >= calculationRowTime(current)) {
      merged.set(key, candidate);
    }
  }

  const rows = [...merged.values()];
  if (!rows.length) return;
  await upsertCalculationRowsIfNewer(rows);
}

async function upsertCalculationRowsIfNewer(rows) {
  if (!supabase || !Array.isArray(rows) || !rows.length) return;
  const { error } = await supabase.rpc('d24_upsert_calculation_results_if_newer', {
    p_rows:rows,
  });
  if (error) throw enhanceMigrationError(error);
}

function cloneStoredCalculationRow(versionId, row) {
  return {
    route_version_id:versionId,
    country_section_order:Number(row.country_section_order),
    country_code:row.country_code || '',
    job_id:row.job_id || crypto.randomUUID(),
    calculator_key:row.calculator_key || '',
    cost_value:finiteNumberOrNull(row.cost_value),
    cost_text:row.cost_text || '',
    currency:row.currency || 'PLN',
    distance_km:finiteNumberOrNull(row.distance_km),
    duration_text:row.duration_text || '',
    raw_result:row.raw_result && typeof row.raw_result === 'object' ? row.raw_result : {},
    screenshot_data_url:row.screenshot_data_url || '',
    status:'completed',
    calculated_at:normalizeCalculationTimestamp(row.calculated_at),
  };
}

function calculationRowKey(sectionOrder, calculatorKey) {
  return `${Number(sectionOrder)}::${String(calculatorKey || '')}`;
}

function calculationRowTime(row) {
  const value = Date.parse(String(row?.calculated_at || ''));
  return Number.isFinite(value) ? value : 0;
}

function normalizeCalculationTimestamp(value) {
  const parsed = Date.parse(String(value || ''));
  return Number.isFinite(parsed) ? new Date(parsed).toISOString() : new Date().toISOString();
}

function calculationResultRow(versionId, section, result) {
  const calculatorKey = result.calculatorKey || section.calculatorKey || '';
  const screenshotPath = String(
    result?.screenshotPath
    || result?.rawResult?.screenshotPath
    || ''
  ).trim();
  const screenshotBucket = String(
    result?.screenshotBucket
    || result?.rawResult?.screenshotBucket
    || (screenshotPath ? DIAGNOSTIC_SCREENSHOT_BUCKET : '')
  ).trim();

  const rawResult = {
    ...stripScreenshots(result),
    ...(screenshotPath ? {
      screenshotBucket:screenshotBucket || DIAGNOSTIC_SCREENSHOT_BUCKET,
      screenshotPath,
    } : {}),
  };

  return {
    route_version_id:versionId,
    country_section_order:section.order,
    country_code:section.countryCode,
    job_id:result.jobId || crypto.randomUUID(),
    calculator_key:calculatorKey,
    cost_value:finiteNumberOrNull(result.costValue),
    cost_text:result.costText || '',
    currency:result.currency || 'PLN',
    distance_km:finiteNumberOrNull(result.distanceKm),
    duration_text:result.durationText || '',
    raw_result:rawResult,

    // Normalny tryb: obraz jest w Supabase Storage, a w bazie tylko bucket/path.
    // Inline Base64 zostaje wyłącznie jako awaryjny fallback, jeżeli upload
    // do Storage faktycznie się nie udał.
    screenshot_data_url:screenshotPath
      ? ''
      : (calculatorKey === 'etoll-pl' ? '' : pickScreenshot(result)),

    status:'completed',
    calculated_at:normalizeCalculationTimestamp(result.calculatedAt || result.calculated_at),
  };
}

export async function hydrateCalculationResultScreenshot(result) {
  if (!result || typeof result !== 'object') return result;
  const screenshot = await loadStoredScreenshotDataUrl(result);
  if (!screenshot) return result;
  return {
    ...result,
    calculatorScreenshot:screenshot,
    screenshotDataUrl:screenshot,
    reportScreenshot:screenshot,
  };
}

async function loadStoredScreenshotDataUrl(result) {
  const inline = pickScreenshot(result);
  if (inline) return inline;
  if (!supabase) return '';

  const path = String(result?.screenshotPath || result?.rawResult?.screenshotPath || '').trim();
  if (!path) return '';

  const bucket = String(result?.screenshotBucket || result?.rawResult?.screenshotBucket || 'etoll-screenshots').trim();
  const { data, error } = await supabase.storage.from(bucket).download(path);
  if (error) {
    console.warn('[D24] Nie udało się pobrać zrzutu e-TOLL ze Storage:', error);
    return '';
  }

  return blobToDataUrl(data);
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Nie udało się odczytać obrazu ze Storage.'));
    reader.readAsDataURL(blob);
  });
}

async function hydrateStoredScreenshots(resultMap) {
  const entries = await Promise.all(Object.entries(resultMap || {}).map(async ([sectionId, result]) => {
    try {
      const screenshot = await loadStoredScreenshotDataUrl(result);
      if (!screenshot) return [sectionId, result];
      return [sectionId, {
        ...result,
        calculatorScreenshot:screenshot,
        screenshotDataUrl:screenshot,
        reportScreenshot:screenshot,
      }];
    } catch (error) {
      console.warn('[D24] Błąd przygotowania zrzutu e-TOLL:', error);
      return [sectionId, result];
    }
  }));
  return Object.fromEntries(entries);
}

function mapResultsToSections(sectionModels, rows) {
  const resultMap = {};
  for (const row of rows || []) {
    const section = sectionModels.find(item => Number(item.order) === Number(row.country_section_order));
    if (!section || resultMap[section.id]) continue;
    const raw = row.raw_result && typeof row.raw_result === 'object' ? row.raw_result : {};
    resultMap[section.id] = {
      ...raw,
      jobId:row.job_id,
      sectionId:section.id,
      sectionOrder:section.order,
      countryCode:row.country_code,
      calculatorKey:row.calculator_key || section.calculatorKey,
      costValue:row.cost_value,
      costText:row.cost_text,
      currency:row.currency,
      distanceKm:row.distance_km,
      durationText:row.duration_text,
      rawResult:raw.rawResult || raw,
      calculatorScreenshot:row.screenshot_data_url,
      screenshotDataUrl:row.screenshot_data_url,
      calculatedAt:row.calculated_at,
    };
  }
  return resultMap;
}

function normalizeLoadedRoute(snapshot, record = {}) {
  return {
    ...snapshot,
    id:snapshot?.id || record.id,
    versionId:snapshot?.versionId || record.current_version_id,
    title:snapshot?.title || record.name || '',
    lineNumber:snapshot?.lineNumber || record.route_number || snapshot?.title || '',
    direction:snapshot?.direction || record.direction || '',
    directionType:normalizeDirectionType(snapshot?.directionType || record.direction_type || inferDirectionType(snapshot?.direction || record.direction)),
    carrier:normalizeCarrier(snapshot?.carrier || record.carrier),
    countries:snapshot?.countries || record.countries || routeCountries(snapshot),
    tollsSummary:snapshot?.tollsSummary || record.tolls_summary || buildTollsSummary(snapshot),
    dirty:false,
  };
}

function routeCountries(route) {
  const countries = [];
  for (const section of route?.sections || []) {
    const value = section?.countryName || section?.countryCode || '';
    if (value && !countries.includes(value)) countries.push(value);
  }
  return countries;
}

function buildTollsSummary(route) {
  const rows = [];
  for (const section of route?.sections || []) {
    const result = route?.results?.[section.id];
    if (!section?.calculatorKey && !result?.calculatorKey && !result) continue;
    rows.push({
      sectionOrder:section.order,
      countryCode:section.countryCode || '',
      countryName:section.countryName || '',
      calculatorKey:result?.calculatorKey || section.calculatorKey || '',
      calculatorName:result?.calculatorName || '',
      costValue:finiteNumberOrNull(result?.costValue),
      costText:result?.costText || '',
      currency:result?.currency || '',
      status:result ? 'completed' : 'pending',
    });
  }
  return rows;
}

function stripScreenshots(result) {
  const copy = { ...result };
  for (const key of [
    'calculatorScreenshot', 'screenshotDataUrl', 'reportScreenshot',
    'routeReportImage', 'screenshot_data_url',
    'mytoResultScreenshotDataUrl', 'mytoMapScreenshotDataUrl',
  ]) delete copy[key];
  return copy;
}

function pickScreenshot(result) {
  for (const value of [
    result?.calculatorScreenshot,
    result?.screenshotDataUrl,
    result?.reportScreenshot,
    result?.routeReportImage,
    result?.screenshot_data_url,
  ]) {
    if (typeof value === 'string' && value.startsWith('data:image/')) return value;
  }
  return '';
}

function combinedDirectionSaveResult(savedByDirection, primaryDirection) {
  const primary = savedByDirection[primaryDirection]
    || savedByDirection.outbound
    || savedByDirection.return
    || {};
  return {
    ...primary,
    outbound_route_id:savedByDirection.outbound?.route_id || null,
    outbound_version_id:savedByDirection.outbound?.version_id || null,
    return_route_id:savedByDirection.return?.route_id || null,
    return_version_id:savedByDirection.return?.version_id || null,
    directions_saved:Object.keys(savedByDirection).length,
  };
}

function uniqueValues(values) {
  return [...new Set((values || []).filter(Boolean))];
}

function normalizeSaveResult(data) {
  if (Array.isArray(data)) return data[0] || {};
  if (data && typeof data === 'object') return data;
  return {};
}

function cleanLineNumber(value) {
  return String(value || '').trim().replace(/\s+/g, ' ').toUpperCase();
}

function deriveLineNumber(value) {
  const text = String(value || '').trim();
  return text.match(/\b[A-Z]{0,4}\d{2,6}[A-Z]?\b/i)?.[0]?.toUpperCase() || text;
}

function normalizeCarrier(value) {
  const carrier = String(value || '').trim().toUpperCase();
  return ['INTER', 'UNIBUS'].includes(carrier) ? carrier : '';
}

function normalizeDirectionType(value) {
  const text = String(value || '').trim().toLowerCase();
  if (['outbound', 'wyjazd', 'tam'].includes(text)) return 'outbound';
  if (['return', 'powrot', 'powrót', 'z powrotem'].includes(text)) return 'return';
  return '';
}

function inferDirectionType(value) {
  const text = String(value || '').toLowerCase();
  return /powr[oó]t|return|z powrotem/.test(text) ? 'return' : 'outbound';
}

function monthBounds(month) {
  const normalized = /^\d{4}-\d{2}$/.test(String(month || ''))
    ? String(month)
    : new Date().toISOString().slice(0, 7);
  const [year, monthNumber] = normalized.split('-').map(Number);
  const start = `${normalized}-01`;
  const next = new Date(Date.UTC(year, monthNumber, 1));
  const end = next.toISOString().slice(0, 10);
  return { start, end };
}

function groupBy(rows, keyFn) {
  const map = new Map();
  for (const row of rows) {
    const key = keyFn(row);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  }
  return map;
}

function readLocalRoutes() {
  try { return JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]'); }
  catch { return []; }
}

function readLocalServiceDays() {
  try { return JSON.parse(localStorage.getItem(LOCAL_SERVICE_DAYS_KEY) || '[]'); }
  catch { return []; }
}

function readLocalCalendarMonths() {
  try { return JSON.parse(localStorage.getItem(LOCAL_CALENDAR_MONTHS_KEY) || '[]'); }
  catch { return []; }
}

function finiteNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(String(value).replace(',', '.'));
  return Number.isFinite(number) ? number : null;
}

function enhanceMigrationError(error) {
  const message = String(error?.message || error || '');
  if (error?.status === 401 || error?.code === 'PGRST301' || error?.code === 'NO_ACTIVE_AUTH_SESSION' || /JWT|401|not authenticated/i.test(message)) {
    const authError = new Error('Sesja Supabase jest nieaktywna albo wygasła. Zaloguj się ponownie w aplikacji.');
    authError.code = error?.code || 'AUTH_REQUIRED';
    authError.status = 401;
    return authError;
  }
  if (/carrier|direction_type|countries|tolls_summary|route_service_days|route_calendar_months|list_route_service_days|replace_route_service_days/i.test(message)) {
    return new Error(`${message} Uruchom pełny schemat Dyspozytor24-Supabase-FRESH-CALENDAR-0.14.3.sql w Supabase SQL Editor.`);
  }
  return error;
}

function isRouteConstraint(point) {
  return Boolean(
    point?.isRouteConstraint
    || point?.manualRouteConstraint
    || point?.pointType === 'route-via'
    || point?.sourceType === 'manual-route-via'
  );
}
